import { colors, cssThemedValue } from '@creditornot/cb-ingredients';
import React from 'react';
import styled, { css } from 'styled-components';

import { Typography as T } from 'common/typography';

import { Section } from './types';
import { BREAKPOINT_MOBILE } from '../breakpoints';

export type Status = 'active' | 'default';

type Item = {
  status?: Status;
  section: Section;
};

type Props = {
  items: Item[];
  getSectionTitleTranslation: (section: Section) => React.ReactNode;
  onStepChange: (section: Section) => void;
};

const BULLET_WIDTH = 40;
const BREAKPOINT_HIDE_LABEL = 350;

const Root = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  padding: 0 12px 48px;
  @media (max-width: ${BREAKPOINT_MOBILE}px) {
    padding: 0 5px 48px;
  }
  @media (max-width: ${BREAKPOINT_HIDE_LABEL}px) {
    padding: 0 5px 5px;
  }
  width: 100%;
  user-select: none;
`;

const Item = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  margin-inline: 8px;
  cursor: pointer;

  ${({ status = 'default' }: { status?: Status }) => css`
    ${Bullet} {
      ${BulletVariants[status]}
    }
    ${Label} {
      ${LabelVariants[status]}
    }
  `}
`;

const Bullet = styled(T.Heading5)`
  width: ${BULLET_WIDTH}px;
  height: ${BULLET_WIDTH}px;
  border-radius: 100%;
  border: 2px solid ${cssThemedValue('borderPrimary')};
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${cssThemedValue('textSecondary')};

  &:hover {
    color: ${cssThemedValue({
      light: colors.salt100,
      dark: colors.pepper100,
    })};
    border-color: ${colors.wolt100};
    background-color: ${colors.wolt100};
  }
`;

const BulletVariants = {
  default: '',
  active: css`
    color: ${cssThemedValue({
      light: colors.salt100,
      dark: colors.pepper100,
    })};
    border-color: ${colors.wolt100};
    background-color: ${colors.wolt100};
  `,
};

const Label = styled(T.Body3)`
  top: ${BULLET_WIDTH}px;
  margin-block-start: 8px;
  width: 100px;
  text-align: center;
  color: ${cssThemedValue('textSecondary')};
  position: absolute;

  @media (max-width: ${BREAKPOINT_MOBILE}px) {
    width: 60px;
    word-wrap: break-word;
  }
  @media (max-width: ${BREAKPOINT_HIDE_LABEL}px) {
    display: none;
  }
`;

const LabelVariants = {
  default: '',
  active: css`
    font-weight: bold;
  `,
};

const Separator = styled.div`
  width: 100%;
  max-width: 96px;
  height: ${BULLET_WIDTH / 2}px;
  border-block-end: 2px dashed ${cssThemedValue('borderPrimary')};
`;

const Stepper = ({ items, getSectionTitleTranslation, onStepChange }: Props) => {
  return (
    <Root>
      {items.map(({ section, status }, index) => (
        <React.Fragment key={`item-${section}`}>
          {index !== 0 && <Separator />}
          <Item status={status} onClick={() => onStepChange(section)}>
            <Bullet as="span">{index + 1}</Bullet>
            <Label>{getSectionTitleTranslation(section)}</Label>
          </Item>
        </React.Fragment>
      ))}
    </Root>
  );
};

export default Stepper;
